import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { makeStyles } from "tss-react/mui";

import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import CloseIcon from "@mui/icons-material/Close";

import { KMKDrawer, ButtonUI, ConfirmPopup, PermanentDeletePopup } from "../../../Common";
import Switch from "../../../Common/Switch";

import DataPipelineService from "../../../../service/dataPipelineService";
import { showToast } from "../../../../store/actions";
import MultipleChipInput from "../../../Common/MultipleChipInput";
import { regexBasicEmail, regexName } from "../../../../util/regularExpress";
import { setPrevState, setStorageValue } from "../../../../util/common";

const drawerWidth = 480;

const emailInputs = [
  { type: "success", label: "Send email on execution success" },
  { type: "failure", label: "Send email on execution failure" },
];

const useStyles = makeStyles()((theme) => ({
  heading: {
    color: "#DF672A",
    fontWeight: "bold",
    fontSize: "1rem",
    "& span": {
      fontWeight: "normal",
    },
  },
  closeIcon: {
    padding: 0,
  },
  container: {
    height: "100%",
    padding: "0px 20px",
  },
  IconHolder: {
    padding: 0,
    margin: "0 2px",
    fontSize: "22px",
    height: "32px",
    width: "32px",
    borderRadius: "64px",
  },
}));

const optionNoJob = { text: "No Entry Job (Remove currently selected)", value: "no_selection" };

const DeleteConfirm = ({ handleCancel, pipelineName, setOpenDeleteConfirm }) => {
  const { Workspace_id, DataPipeline_id } = useParams();
  const [loadingForConfirm, setLoadingForConfirm] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { User_id } = useSelector((state) => state.user.currentUser);

  const deletePipeline = async () => {
    setLoadingForConfirm(true);

    const pipeline = new DataPipelineService();
    const response = await pipeline.deleteDataPipeline(Workspace_id, DataPipeline_id);
    if (response?.success) {
      setStorageValue(`${User_id}_PipelineFilters`, {}, (storedValue) => {
        const updatedFilters = {
          ...storedValue,
          [Workspace_id]: {
            ...storedValue[Workspace_id],
            selectedPipelines:
              storedValue[Workspace_id]?.selectedPipelines?.filter((id) => id !== parseInt(DataPipeline_id)) || [],
          },
        };

        return updatedFilters;
      });

      dispatch(showToast({ message: response.message, type: "success" }));
      setOpenDeleteConfirm(false);
      navigate(`/my-workspaces/${Workspace_id}/scheduled-jobs`);
    }

    setLoadingForConfirm(false);
  };

  return (
    <PermanentDeletePopup
      item={"Pipeline"}
      title={
        <>
          <span style={{ fontWeight: "bold" }}>{pipelineName}</span> will be permanently removed. This will remove all
          the jobs in the data pipeline. This action is irreversible.
        </>
      }
      message={`Are you sure you want to delete "${pipelineName}" Data Pipeline?`}
      handleDelete={deletePipeline}
      handleCancel={handleCancel}
      loadingForConfirm={loadingForConfirm}
    />
  );
};

const SettingsPanel = ({ defaultValues, handleClose, onSave }) => {
  const { classes } = useStyles();
  const { Workspace_id, DataPipeline_id } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { workspace } = useSelector((state) => state.workspace);
  const { User_id } = useSelector((state) => state?.user?.currentUser);
  const { accessRole } = workspace;

  const { entryJobs, pipelineName, isDefault, entryPoint, successEnable, successEmails, errorEnable, errorEmails } =
    defaultValues;

  const [name, setName] = useState(pipelineName);
  const [entryJob, setEntryJob] = useState(entryPoint);
  const [isDefaultPipeline, setIsDefaultPipeline] = useState(isDefault);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);

  const [openCloneConfirm, setOpenCloneConfirm] = useState({
    open: false,
    confirmTitle: "Clone Data Pipeline",
    confirmMessage: "",
    loadingForConfirm: false,
  });

  // For Managing states
  const [emailEnable, setEmailEnable] = useState({ success: successEnable || false, failure: errorEnable || false });
  const [emails, setEmails] = useState({ success: successEmails || [], failure: errorEmails || [] });
  const [inputValues, setInputValues] = useState({ success: "", failure: "" });

  const $jobs = useMemo(() => {
    const _entryJobs = [...entryJobs];
    _entryJobs.sort((a, b) => a.text.localeCompare(b.text));

    return entryJob ? [optionNoJob, ..._entryJobs] : _entryJobs;
  }, [entryJob, entryJobs]);

  const handleCancel = () => {
    setOpenDeleteConfirm(false);
  };

  const handleMakeClone = async () => {
    setOpenCloneConfirm((prevState) => ({
      ...prevState,
      open: true,
      confirmMessage: (
        <Box sx={{ maxWidth: "600px" }}>
          <Typography>{`Are you sure you want to clone the current pipeline?`}</Typography>
        </Box>
      ),
    }));
  };

  const makeClonePipeline = async () => {
    setOpenCloneConfirm((prevState) => ({
      ...prevState,
      loadingForConfirm: true,
    }));
    const pipeline = new DataPipelineService();

    const response = await pipeline.makeClone(Workspace_id, DataPipeline_id);

    if (response?.success) {
      dispatch(showToast({ message: response.message, type: "success" }));
      setOpenCloneConfirm((prevState) => ({
        ...prevState,
        open: false,
        loadingForConfirm: false,
      }));

      setStorageValue(`${User_id}_PipelineFilters`, {}, (storedValue) => {
        let selectedPipelines = storedValue[Workspace_id]?.["selectedPipelines"] || [];
        if (selectedPipelines?.length) selectedPipelines?.push(response.data.DataPipeline_id);
        return { ...storedValue, [Workspace_id]: { ...storedValue[Workspace_id], selectedPipelines } };
      });

      navigate(`/my-workspaces/${Workspace_id}/scheduled-jobs/data-pipeline/${response.data.DataPipeline_id}`);
    }

    setOpenCloneConfirm((prevState) => ({
      ...prevState,
      open: false,
      loadingForConfirm: false,
    }));
  };

  const isSaveDisabled = useMemo(() => {
    if (!name) return true;
    if (emailEnable.success && !emails.success?.length) return true;
    if (emailEnable.failure && !emails.failure?.length) return true;
    return false;
  }, [emailEnable.failure, emailEnable.success, emails.failure?.length, emails.success?.length, name]);

  const handleEmailChange = useCallback((type, newValue) => {
    const lastEmail = newValue[newValue.length - 1];
    if (regexBasicEmail(lastEmail)) setPrevState(setEmails, { [type]: newValue });
    else setPrevState(setInputValues, { [type]: lastEmail || "" });
  }, []);

  const handleInputChange = useCallback((type, newValue) => {
    setPrevState(setInputValues, { [type]: newValue });
  }, []);

  const handleClear = useCallback((type) => {
    setPrevState(setEmails, { [type]: [] });
    setPrevState(setInputValues, { [type]: "" });
  }, []);

  const handleCheckbox = useCallback(
    (type) => setPrevState(setEmailEnable, { [type]: !emailEnable?.[type] }),
    [emailEnable]
  );

  const onDelete = useCallback(
    (type, option) => setEmails((prev) => ({ ...prev, [type]: prev[type].filter((email) => email !== option) })),
    []
  );

  const processEmails = useCallback(
    (emailString, type) => {
      const emailArray = emailString.split(",").map((email) => email.trim());

      let newEmails = [...emails?.[type]];
      emailArray.forEach((email) => {
        if (regexBasicEmail(email) && !newEmails.includes(email)) newEmails.push(email.toLowerCase());
      });

      setPrevState(setEmails, { [type]: newEmails });
      setPrevState(setInputValues, { [type]: "" });
    },
    [emails]
  );

  const handleKeyPress = useCallback(
    (event, type) => {
      const trimmedValue = inputValues?.[type]?.trim();
      if (
        event.key === "Enter" &&
        emails[type]?.some((email) => email?.toLowerCase() === trimmedValue?.toLowerCase())
      ) {
        event.preventDefault();
        setPrevState(setInputValues, { [type]: "" });
      }
      if (event.key === ",") {
        event.preventDefault();
        if (trimmedValue) processEmails(trimmedValue, type);
      }
    },
    [emails, inputValues, processEmails]
  );

  const handlePaste = useCallback(
    (event, type) => {
      const pastedData = event.clipboardData.getData("text");
      event.preventDefault();
      if (pastedData) processEmails(pastedData, type);
    },
    [processEmails]
  );

  const handleSave = () =>
    onSave({
      pipelineName: name,
      entryPoint: entryJob === "no_selection" ? "" : entryJob,
      isDefault: isDefaultPipeline,
      successEnable: emailEnable.success || false,
      successEmails: emails.success || [],
      errorEnable: emailEnable.failure || false,
      errorEmails: emails.failure || [],
    });

  return (
    <KMKDrawer
      anchor="right"
      open={true}
      sx={{
        width: drawerWidth,
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: "450px" },
          boxSizing: "border-box",
        },
      }}
    >
      <Toolbar />
      <Box sx={{ display: "flex", alingItems: "center", justifyContent: "space-between", mx: 3 }}>
        <Typography className={classes.heading} variant="h6" component="div">
          Pipeline Settings
        </Typography>
        <IconButton className={classes.IconHolder} onClick={handleClose} sx={{ mx: 1 }}>
          <CloseIcon fontSize="inherit" color="primary" />
        </IconButton>
      </Box>
      <Box className={classes.container} role="presentation" sx={{ maxHeight: "100%", overflowY: "auto" }}>
        <Grid container spacing={0}>
          <Grid size={12}>
            <TextField
              required
              autoFocus
              value={name}
              inputProps={{ maxLength: 30 }}
              onChange={(e) => {
                const isValid = regexName(e.target.value, "jobName", "_ -");
                if (isValid) setName(e.target.value);
              }}
              fullWidth
              label="Data Pipeline"
              variant="outlined"
              sx={{ my: 2 }}
            />
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth variant="outlined" sx={{ my: 1 }}>
              <InputLabel>Entry Point</InputLabel>
              <Select value={entryJob} onChange={(e) => setEntryJob(e.target.value)} label="Entry Point">
                {$jobs.map((job) => (
                  <MenuItem key={job.value} value={job.value} disabled={job.value === "NoOption"}>
                    {job.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <FormGroup aria-label="position" row>
              <FormControlLabel
                value={!!isDefaultPipeline}
                control={<Switch className={classes.Switch} />}
                label="Set as Default"
                labelPlacement="end"
                sx={{ my: 1 }}
                onChange={() => setIsDefaultPipeline((prev) => !prev)}
                checked={!!isDefaultPipeline}
                disabled={isDefaultPipeline || !DataPipeline_id}
              />
            </FormGroup>
          </Grid>

          {emailInputs.map(({ type, label }) => (
            <Grid key={type} size={12}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <FormControlLabel
                  control={<Checkbox checked={emailEnable?.[type] || false} />}
                  label={label}
                  onChange={() => handleCheckbox(type)}
                />
                <ButtonUI
                  variant="text"
                  color="secondary"
                  style={{ margin: "7px  10px 7px 0", fontSize: "14px" }}
                  onMouseDown={(e) => handleClear(type)}
                  disabled={!emails?.[type]?.length || false}
                >
                  Clear
                </ButtonUI>
              </Box>
              <MultipleChipInput
                placeholder="Enter Email Recipients"
                value={emails?.[type] || []}
                inputValue={inputValues?.[type] || ""}
                helperText="Press Enter or use commas to add email addresses separately."
                handleonInputChange={(event, newInputValue) => handleInputChange(type, newInputValue)}
                handleOnChange={(event, newValue) => handleEmailChange(type, newValue)}
                onDelete={(option) => onDelete(type, option)}
                handleKeyPress={(e) => handleKeyPress(e, type)}
                handlePaste={(e) => handlePaste(e, type)}
                disableClearable={true}
              />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} mb={3} mt={1}>
          <Grid size={12}>
            <ButtonUI disabled={isSaveDisabled} variant="contained" color="success" onClick={handleSave}>
              Save
            </ButtonUI>
            <ButtonUI variant="contained" color="secondary" style={{ marginLeft: 15 }} onClick={handleClose}>
              Cancel
            </ButtonUI>
            {[1, 2].includes(accessRole) && (
              <ButtonUI
                disabled={!name}
                variant="contained"
                style={{ marginLeft: 15 }}
                color="info"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMakeClone();
                }}
              >
                Clone
              </ButtonUI>
            )}
            {[1, 2].includes(accessRole) && isDefault !== 1 && (
              <ButtonUI
                variant="contained"
                color="error"
                style={{ marginLeft: 15 }}
                onClick={(e) => setOpenDeleteConfirm(true)}
              >
                Delete
              </ButtonUI>
            )}
          </Grid>
        </Grid>
      </Box>
      {openCloneConfirm.open && (
        <ConfirmPopup
          configData={{
            popupTitle: openCloneConfirm.confirmTitle,
            popupMessage: openCloneConfirm.confirmMessage,
            handleCancel: () => setOpenCloneConfirm((prevState) => ({ ...prevState, open: false })),
            handleClick: () => makeClonePipeline(),
            loadingForConfirm: openCloneConfirm.loadingForConfirm,
          }}
        />
      )}
      {openDeleteConfirm && (
        <DeleteConfirm
          handleCancel={handleCancel}
          pipelineName={pipelineName}
          setOpenDeleteConfirm={setOpenDeleteConfirm}
        />
      )}
    </KMKDrawer>
  );
};

export default React.memo(SettingsPanel);
